import { worthBuyingAgain } from "./buy-again.js";
import { evaluateDrinkingWindow } from "./drinking-window.js";
import { foldAccents, normaliseName } from "./matching.js";
/** The unnarrowed list: every wine, in the order it arrived. */
export const noWineFilter = {
    query: "",
    type: null,
    countryCode: null,
    window: null,
    format: null,
    storage: null,
    shelf: null,
    stock: "any",
};
/**
 * The list as it opens.
 *
 * The one narrowing that is on before anybody asks for it. A cellar accretes
 * rows that are finished — 116 wines of which a third may be drunk dry — and
 * the list exists to answer *where is that bottle*, a question no empty row
 * can answer. So the default hides them, and the count above the list says
 * how many are hidden with one tap to show them.
 *
 * Named here rather than in the screen that reads the URL, because a default
 * written down twice is a default that will eventually be two.
 */
export const defaultWineFilter = {
    ...noWineFilter,
    stock: "inCellar",
};
/** How many bottles of this wine are still owned, standing or out. */
function bottlesLeft(wine) {
    return wine.standing.reduce((total, place) => total + place.count, 0) + wine.away;
}
/**
 * Whether this wine is still owned.
 *
 * A bottle away at a dinner counts — `project-scope.md` says so everywhere
 * else, and the stock filter above reads it the same way — because it is
 * owned, it is coming back, and a window that closes this year closes on it
 * too. A wine drunk dry is another matter: it is not a bottle to open, and a
 * page telling somebody to hurry up with a wine they finished last spring is
 * the fastest way to stop a page being read.
 *
 * Here rather than on a screen because two of them ask it — *Att öppna*, of
 * every row it groups, and *Till maten*, of whether there is anything to ask
 * about at all — and a predicate that decides what a page leaves out should
 * not be written twice.
 */
export function stillOwned(wine) {
    return bottlesLeft(wine) > 0;
}
/**
 * Whether the list is narrowed by anything beyond what it opens with — which
 * is what decides *Rensa filter*, the way back to `defaultWineFilter`.
 *
 * Asked for by the owner on 2026-10-07: clearing the filter leaves *Finns i
 * källaren* chosen and nothing else, rather than showing every wine. The
 * finished wines are one choice away under *Lager*, which is where a reader
 * looking for them looks; a clear that also undid the list's own default
 * cleared one thing too many. So on a list in its resting state there is
 * nothing to clear, and the button is not drawn.
 */
export function wineFilterIsDefault(criteria) {
    return (criteria.query.trim() === "" &&
        criteria.type === null &&
        criteria.countryCode === null &&
        criteria.window === null &&
        criteria.format === null &&
        criteria.storage === null &&
        criteria.shelf === null &&
        criteria.stock === defaultWineFilter.stock);
}
/**
 * Whether anything is being narrowed at all, the default included.
 *
 * True on a freshly opened list, because the freshly opened list is narrowed:
 * it hides the wines that are finished. That is deliberate rather than an
 * oversight — a default that quietly withholds rows should say so and offer
 * the way out, which is exactly what this makes the bar do.
 */
export function wineFilterIsActive(criteria) {
    return (criteria.query.trim() !== "" ||
        criteria.type !== null ||
        criteria.countryCode !== null ||
        criteria.window !== null ||
        criteria.format !== null ||
        criteria.storage !== null ||
        criteria.shelf !== null ||
        criteria.stock !== "any");
}
/**
 * The words a wine is found by: what is written on its label and on the
 * shelf tag, accents folded so `hebrart` finds `Hébrart` — someone typing a
 * producer's name on a phone in a dark cellar should not have to reach the
 * accent menu to find their own bottle.
 *
 * `nv` is in here on purpose. It is what the interface calls a non-vintage
 * wine and therefore what someone types looking for one, and a third of this
 * cellar is Champagne.
 */
function words(wine) {
    return foldAccents(normaliseName([
        wine.name,
        wine.producer,
        wine.region ?? "",
        ...wine.grapes,
        wine.isNonVintage ? "NV" : "",
    ].join(" ")));
}
/**
 * The numbers a wine answers to. Matched whole rather than as substrings,
 * which is the difference between typing `47` and getting wine 47, and typing
 * `47` and getting every wine from 1947 as well. The cellar number is the one
 * on the label of the bottle in your hand, so it has to land exactly.
 */
function numbers(wine) {
    return wine.vintage === null ? [wine.number] : [wine.number, wine.vintage];
}
/**
 * Whether one wine answers to the query. Every term has to match something —
 * `hebrart 2018` means both, not either — and a term is matched as a whole
 * number when it is written as digits and as a substring otherwise, so a
 * half-typed producer narrows the list while a half-typed year does not
 * quietly match a different one.
 */
function matchesQuery(wine, query) {
    const terms = foldAccents(normaliseName(query))
        .split(" ")
        .filter((term) => term !== "");
    if (terms.length === 0)
        return true;
    const text = words(wine);
    const figures = numbers(wine);
    // The article number is matched whole and as text, never as a substring and
    // never as a number. Whole, because it is seven digits and a substring of it
    // would put this wine under half the numbers anybody types — `47` would find
    // wine 47 and every receipt with a 47 in it. Text, because a leading nought
    // is part of an identifier, and because a shop other than Systembolaget may
    // print letters in one.
    const articleNumber = wine.systembolagetId == null
        ? null
        : foldAccents(normaliseName(wine.systembolagetId));
    return terms.every((term) => term === articleNumber ||
        (/^\d+$/u.test(term)
            ? figures.includes(Number(term)) || text.includes(term)
            : text.includes(term)));
}
/**
 * The wines that match, in the order they were given. Order is the caller's:
 * the list arrives sorted by cellar number, and a filter that also reordered
 * would move a row out from under the thumb that was reaching for it.
 */
export function filterWines(wines, criteria, currentYear) {
    return wines.filter((wine) => {
        if (criteria.stock !== "any") {
            const left = bottlesLeft(wine) > 0;
            if (left !== (criteria.stock === "inCellar"))
                return false;
            if (criteria.stock === "buyAgain" && !worthBuyingAgain(wine.ratings ?? [])) {
                return false;
            }
        }
        if (criteria.format !== null && !wine.formats.includes(criteria.format)) {
            return false;
        }
        if ((criteria.storage !== null || criteria.shelf !== null) &&
            !wine.standing.some((place) => (criteria.storage === null || place.storageName === criteria.storage) &&
                (criteria.shelf === null || place.shelfName === criteria.shelf))) {
            return false;
        }
        if (criteria.type !== null && wine.type !== criteria.type)
            return false;
        if (criteria.countryCode !== null && wine.countryCode !== criteria.countryCode) {
            return false;
        }
        if (criteria.window !== null &&
            evaluateDrinkingWindow(wine, currentYear) !== criteria.window) {
            return false;
        }
        return matchesQuery(wine, criteria.query);
    });
}
//# sourceMappingURL=wine-filter.js.map